import { useMemo } from 'react';
import { useRosterView } from '@/api/views';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useActiveStudio } from '@/state/useActiveStudio';
import { ALL_STUDIOS_SCOPE, isAllStudios, studioOptions } from '@/domain/studioScope';
import { buildArtistLeaderboard } from '@/components/leaderboard/leaderboardData';
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable';
import { useNavigate } from 'react-router-dom';
import { Tv } from 'lucide-react';
import type { StudioId } from '@/domain/types';

export default function Leaderboard() {
  // Live reads (tv-ods.3): standings read all bookings/artists/studios from the
  // network-wide roster view (/api/views/roster + /ws), not brainStore.
  const roster = useRosterView();
  const bookings = roster.data?.bookings ?? {};
  const artists = roster.data?.artists ?? {};
  const studios = roster.data?.studios ?? {};
  const clockIso = useSimulationStore((s) => s.clockIso);
  const navigate = useNavigate();
  // Studio scope follows the top-bar switcher (the single source of truth) instead
  // of a screen-local toggle. An unset/'all' selection reads network-wide.
  const { activeStudio, assignedStudios, isMultiStudio } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);
  const isAll = isAllStudios(activeStudio);
  const scopedStudio = isAll ? undefined : (activeStudio as StudioId);
  // Only chips for studios this persona is assigned to (the switcher's own gate).
  // branding-3: archived studios drop out of the chips (the current pick stays)
  // and their artists out of the network-wide board.
  const selectableStudios = studioOptions(studios, assignedStudios, activeStudio);
  const liveArtists = useMemo(
    () => Object.fromEntries(Object.entries(artists).filter(([, a]) => !studios[a.studioId]?.archived)),
    [artists, studios],
  );

  const rows = useMemo(
    () => buildArtistLeaderboard(bookings, liveArtists, clockIso, scopedStudio),
    [bookings, liveArtists, clockIso, scopedStudio],
  );

  return (
    <section className="px-8 py-8 max-w-5xl" data-testid="leaderboard-screen">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="label-small-caps text-gold-bright">P5 · Studio Health</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Rebook Leaderboard</h1>
          <p className="font-serif text-cream-soft mt-2">
            Artists ranked by in-chair rebooks this month. Streaks count consecutive completed
            sessions that ended with the next one already locked in.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            const target = isAll ? selectableStudios[0]?.id : scopedStudio;
            if (target) navigate(`/leaderboard/tv/${target}`);
          }}
          className="btn-quiet text-sm"
          data-testid="leaderboard-tv-launch"
        >
          <Tv size={14} /> Launch TV mode
        </button>
      </header>

      <div className="flex gap-2 flex-wrap mb-4" data-testid="leaderboard-studio-filter">
        {isMultiStudio ? (
          <FilterChip
            active={isAll}
            onClick={() => setActiveStudio(ALL_STUDIOS_SCOPE)}
            label="All studios"
            testid="leaderboard-filter-all"
          />
        ) : null}
        {selectableStudios.map((s) => (
          <FilterChip
            key={s.id}
            active={activeStudio === s.id}
            onClick={() => setActiveStudio(s.id)}
            label={s.name}
            testid={`leaderboard-filter-${s.id}`}
          />
        ))}
      </div>

      <LeaderboardTable rows={rows} />
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        active
          ? 'rounded-md border border-gold-bright bg-gold/10 text-gold-bright font-inter text-[11px] px-2.5 py-1.5 uppercase tracking-widest'
          : 'rounded-md border border-gold-dim/30 text-cream-soft hover:border-gold-bright/50 font-inter text-[11px] px-2.5 py-1.5 uppercase tracking-widest transition'
      }
    >
      {label}
    </button>
  );
}
